import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import apiService from '../../services/api.service'
import { useSelector } from 'react-redux'

/**
 * Every role that exists.
 *
 * The account tabs and the role dropdown are built from this rather than from a list
 * in the code - roles are CRUD-able now, and a hardcoded list is how `admin`,
 * `finance` and `insurance` came to have no tab at all.
 */
export const getRoleList = createAsyncThunk(
    'getRoleList',
    async (payload, thunkAPI) => {
        try {
            return await apiService.getRoleList(payload)
        } catch (error) {
            return thunkAPI.rejectWithValue(error)
        }
    }
)

// Role CRUD. Superadmin only; the API enforces it, these just carry the calls.
const roleThunk = (name, method) =>
    createAsyncThunk(name, async (payload, thunkAPI) => {
        try {
            return await apiService[method](payload)
        } catch (error) {
            return thunkAPI.rejectWithValue(error)
        }
    })

export const createRole = roleThunk('createRole', 'createRole')
export const updateRole = roleThunk('updateRole', 'updateRole')
export const deleteRole = roleThunk('deleteRole', 'deleteRole')
export const resetUserPassword = roleThunk('resetUserPassword', 'resetUserPassword')
export const deleteUser = roleThunk('deleteUser', 'deleteUser')

export const getUserList = createAsyncThunk(
    'getUserList',
    async (payload, thunkAPI) => {
        try {
            const response = await apiService.getUserList(payload)
            return response
        } catch (error) {
            return thunkAPI.rejectWithValue(error)
        }
    }
)

export const createUser = createAsyncThunk(
    'createUser',
    async (payload, thunkAPI) => {
        try {
            const response = await apiService.createUser(payload)
            return response
        } catch (error) {
            return thunkAPI.rejectWithValue(error)
        }
    }
)

export const getUserInfo = createAsyncThunk(
    'getUserInfo',
    async (payload, thunkAPI) => {
        try {
            const response = await apiService.getUserInfo(payload)
            return response
        } catch (error) {
            return thunkAPI.rejectWithValue(error)
        }
    }
)

export const updateUser = createAsyncThunk(
    'updateUser',
    async (payload, thunkAPI) => {
        try {
            const response = await apiService.updateUser(payload)
            return response
        } catch (error) {
            return thunkAPI.rejectWithValue(error)
        }
    }
)

export const toggleUser = createAsyncThunk(
    'toggleUser',
    async (payload, thunkAPI) => {
        try {
            const response = await apiService.toggleUser(payload)
            return response
        } catch (error) {
            return thunkAPI.rejectWithValue(error)
        }
    }
)

export const updatePassword = createAsyncThunk(
    'updatePassword',
    async (payload, thunkAPI) => {
        try {
            const response = await apiService.updatePassword(payload)
            return response
        } catch (error) {
            return thunkAPI.rejectWithValue(error)
        }
    }
)

export const initialData = {
    roleList: [],
    // The role screen had no loading state at all, so it rendered an empty table
    // while the list was on its way - which reads as "there are no roles".
    roleListLoading: false,
    userSort: {
        active_page: 1,
        start: 0,
        length: 10,
        search: '',
        toggle_loading: false,
        type: 'operation',
        // Empty means the backend's own order: newest first.
        sort: '',
        order: '',
    },
    userData: {
        list: [],
        total: 0,
        loading: false,
    },

    formLoading: false,
    updateLoading: false,
    createLoading: false,
}

const dataSlice = createSlice({
    name: 'user/data',
    initialState: initialData,
    reducers: {
        setUserSort: (state, action) => {
            state.userSort = action.payload
        },
        clearUserSort: (state, action) => {
            state.userSort = {
                active_page: 1,
                start: 0,
                length: 10,
                search: '',
                toggle_loading: false,
                type: 'operation',
                sort: '',
                order: '',
            }
        },
    },
    extraReducers: {
        [getRoleList.pending]: (state) => {
            state.roleListLoading = true
        },
        [getRoleList.fulfilled]: (state, action) => {
            state.roleListLoading = false
            // The API already leaves superadmin out.
            state.roleList = action.payload?.data ?? []
        },
        [getRoleList.rejected]: (state) => {
            state.roleListLoading = false
        },

        //list
        [getUserList.rejected]: (state) => {
            state.userData.loading = false
            state.userSort.toggle_loading = false
        },
        [getUserList.fulfilled]: (state, action) => {
            state.userData.loading = false
            state.userSort.toggle_loading = false

            state.userData.list = action.payload.data.aaData
            state.userData.total = action.payload.data.iTotalDisplayRecords
        },
        [getUserList.pending]: (state) => {
            state.userData.loading = true
            state.userSort.toggle_loading = false
        },

        //info
        [getUserInfo.rejected]: (state) => {
            state.formLoading = false
        },
        [getUserInfo.fulfilled]: (state, action) => {
            state.formLoading = false
            state.userInfo = action.payload.data
        },
        [getUserInfo.pending]: (state) => {
            state.formLoading = true
        },

        //create
        [createUser.rejected]: (state) => {
            state.createLoading = false
        },
        [createUser.fulfilled]: (state, action) => {
            state.createLoading = false
        },
        [createUser.pending]: (state) => {
            state.createLoading = true
        },

        //update
        [updateUser.rejected]: (state) => {
            state.updateLoading = false
        },
        [updateUser.fulfilled]: (state, action) => {
            state.updateLoading = false
        },
        [updateUser.pending]: (state) => {
            state.updateLoading = true
        },

        //toggle
        [toggleUser.rejected]: (state) => {
            state.userData.loading = false
            state.userSort.toggle_loading = false
        },
        [toggleUser.fulfilled]: (state, action) => {
            state.userData.loading = true
            state.userSort.toggle_loading = false
        },
        [toggleUser.pending]: (state) => {
            state.userData.loading = true
            state.userSort.toggle_loading = true
        },

        // [updatePassword.rejected]: (state) => {
        //     state.updateLoading = false
        // },
        // [updatePassword.fulfilled]: (state, action) => {
        //     state.updateLoading = false
        // },
        // [updatePassword.pending]: (state) => {
        //     state.updateLoading = true
        // },
    },
})

export const { setUserSort, clearUserSort } = dataSlice.actions

export default dataSlice.reducer
